import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const navigate = useNavigate();
  const pollingAttempts = useRef(0);
  const maxAttempts = 5;

  const checkPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your transactions.');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        withCredentials: true
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setTransaction(response.data.transaction);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        toast.error('Payment session expired');
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      logger.error('Failed to check payment status:', error);
      setStatus('error');
      toast.error('Failed to verify payment');
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/marketplace');
      return;
    }
    checkPaymentStatus(sessionId);
  }, [searchParams, navigate, checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-3xl bg-white border border-stone-100 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {status === 'checking' && (
            <>
              <Loader2 className="h-20 w-20 text-primary mx-auto mb-6 animate-spin" />
              <h2 className="text-3xl font-heading font-bold mb-4">Verifying Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your purchase</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-accent/10 mb-6">
                <CheckCircle2 className="h-16 w-16 text-accent" />
              </div>
              <h2 className="text-3xl font-heading font-bold mb-4">Payment Successful!</h2>
              <p className="text-muted-foreground mb-8">
                Thank you for your purchase. The seller will be notified.
              </p>
              {transaction && (
                <div className="rounded-2xl bg-stone-50 p-6 mb-8 text-left">
                  <h3 className="font-heading font-semibold mb-3">Transaction Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">${transaction.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transaction ID:</span>
                      <span className="font-mono text-xs">{transaction.transaction_id}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex space-x-4 justify-center">
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="rounded-full bg-primary text-primary-foreground"
                >
                  Browse More Yarns
                </Button>
                <Button
                  onClick={() => navigate('/inventory')}
                  variant="outline"
                  className="rounded-full"
                >
                  View My Inventory
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-destructive/10 mb-6">
                <span className="text-5xl">⚠️</span>
              </div>
              <h2 className="text-3xl font-heading font-bold mb-4">Verification Error</h2>
              <p className="text-muted-foreground mb-8">
                We couldn&apos;t verify your payment. Please contact support.
              </p>
              <Button
                onClick={() => navigate('/marketplace')}
                className="rounded-full bg-primary text-primary-foreground"
              >
                Back to Marketplace
              </Button>
            </>
          )}

          {(status === 'expired' || status === 'timeout') && (
            <>
              <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-yellow-100 mb-6">
                <span className="text-5xl">⏱️</span>
              </div>
              <h2 className="text-3xl font-heading font-bold mb-4">Session Expired</h2>
              <p className="text-muted-foreground mb-8">
                The payment session has expired. Please try again.
              </p>
              <Button
                onClick={() => navigate('/marketplace')}
                className="rounded-full bg-primary text-primary-foreground"
              >
                Back to Marketplace
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
